import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('recupnet_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('recupnet_token');
      localStorage.removeItem('recupnet_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Cases
export const casesAPI = {
  create: (data) => api.post('/cases', data),
  list: (params) => api.get('/cases', { params }),
  get: (id) => api.get(`/cases/${id}`),
  signTerm: (id, data) => api.post(`/cases/${id}/sign-term`, data),
  getTerm: (id) => api.get(`/cases/${id}/term`),
};

// Documents
export const documentsAPI = {
  upload: (caseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cases/${caseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (caseId) => api.get(`/cases/${caseId}/documents`),
  getFileUrl: (docId) => `${API_BASE}/documents/${docId}/file`,
  approve: (docId) => api.post(`/admin/documents/${docId}/approve`),
  reject: (docId, reason) => api.post(`/admin/documents/${docId}/reject`, { reason }),
};

// Admin
export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  listClients: (params) => api.get('/admin/clients', { params }),
  createClient: (data) => api.post('/admin/clients', data),
  clientReport: (params) => api.get('/admin/clients/report', { params }),
  clientFiles: (clientId) => api.get(`/admin/clients/${clientId}/files`),
  exportClients: (clientIds) => api.post('/admin/clients/export', { clientIds }, { responseType: 'blob' }),
  deletionInfo: (clientId) => api.get(`/admin/clients/${clientId}/deletion-info`),
  deleteClient: (clientId, data) => api.post(`/admin/clients/${clientId}/delete`, data),
  restoreClient: (clientId) => api.post(`/admin/clients/${clientId}/restore`),
  trashClients: (params) => api.get('/admin/clients/trash', { params }),
  uploadSerasa: (clientId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/clients/${clientId}/serasa`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSerasaFileUrl: (reportId) => `${API_BASE}/admin/serasa/${reportId}/file`,
  sendList: (listId, data) => api.post(`/admin/export-lists/${listId}/send`, data),
  // Listas V2
  criarLista: (data) => api.post('/admin/listas', data),
  listarListas: () => api.get('/admin/listas'),
  getLista: (id) => api.get(`/admin/listas/${id}`),
  enviarLista: (id) => api.post(`/admin/listas/${id}/enviar`),
  protocolarLista: (id, data) => api.post(`/admin/listas/${id}/protocolar`, data),
  baixarTodosEmpresa: (loteId, data) => api.post(`/admin/lotes/${loteId}/baixar-todos`, data),
  loteDetalhe: (loteId) => api.get(`/admin/lotes/${loteId}/detalhe`),
  clientesElegiveis: (params) => api.get('/admin/listas-elegiveis', { params }),
  eligibleCasesV2: () => api.get('/admin/eligible-cases-v2'),
  createExportList: (data) => api.post('/admin/export-lists', data),
  listExportLists: () => api.get('/admin/export-lists'),
  getExportList: (id) => api.get(`/admin/export-lists/${id}`),
  markSent: (id) => api.post(`/admin/export-lists/${id}/mark-sent`),
  downloadXlsx: (id) => `${API_BASE}/admin/export-lists/${id}/download-xlsx`,
  downloadZip: (id) => `${API_BASE}/admin/export-lists/${id}/download-zip`,
  sentLists: () => api.get('/admin/sent-lists'),
  retornoPipeline: () => api.get('/admin/retorno-pipeline'),
  recordReturn: (data) => api.post('/admin/returns', data),
  addMilestone: (caseId, data) => api.post(`/admin/cases/${caseId}/milestones`, data),
  // Protocolo
  protocolarIndividual: (data) => api.post('/admin/protocolo/individual', data),
  protocolarLote: (data) => api.post('/admin/protocolo/lote', data),
  // Baixas
  baixaIndividual: (data) => api.post('/admin/baixa/individual', data),
  baixaLote: (data) => api.post('/admin/baixa/lote', data),
  // Baixas concluidas
  baixasConcluidas: (params) => api.get('/admin/baixas-concluidas', { params }),
  exportConcluidas: () => api.post('/admin/baixas-concluidas/export', {}, { responseType: 'blob' }),
  pendingDocuments: (params) => api.get('/admin/pending-documents', { params }),
  bulkApprove: (documentIds) => api.post('/admin/documents/bulk-approve', { documentIds }),
  bulkReject: (documentIds, reason) => api.post('/admin/documents/bulk-reject', { documentIds, reason }),
};

// Cases extended
export const casesExtAPI = {
  pendencias: (caseId) => api.get(`/cases/${caseId}/pendencias`),
  milestoneHistory: (caseId) => api.get(`/cases/${caseId}/milestone-history`),
};

// Client
export const clientAPI = {
  dashboard: () => api.get('/client/dashboard'),
};

export default api;
